import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, RefreshCw, CheckCircle2, AlertTriangle, Wrench, ExternalLink, Play, Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { startAudit, auditStatus, auditResults, startDeepAudit, deepAuditStatus, coherenceScan, coherenceFix, accuracySlo, verifyAdmin, setAdminKey, hasAdminKey } from "../lib/api";

function AdminGate({ onUnlock }) {
  const [key, setKey] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await verifyAdmin(key);
      setAdminKey(key);
      onUnlock();
    } catch {
      setErr("Invalid admin key.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#FAF9F5]">
      <Navbar />
      <div className="mx-auto flex max-w-md flex-col items-center px-4 pb-24 pt-28 text-center">
        <div className="rounded-full border border-[#e8e6df] bg-white p-4 shadow-sm">
          <ShieldCheck className="h-8 w-8 text-[#157a59]" />
        </div>
        <h1 className="mt-5 font-heading text-3xl font-bold text-[#1a1a18]">Internal accuracy console</h1>
        <p className="mt-2 text-sm text-neutral-500">
          This page contains internal verification tooling and is restricted to administrators.
        </p>
        <form onSubmit={submit} className="mt-6 w-full space-y-3">
          <input type="password" value={key} onChange={(e) => setKey(e.target.value)}
            placeholder="Admin key" data-testid="admin-key-input" autoFocus
            className="w-full rounded-md border border-[#e8e6df] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#157a59]" />
          {err && <p data-testid="admin-key-error" className="text-sm text-red-600">{err}</p>}
          <button type="submit" disabled={busy || !key} data-testid="admin-key-submit"
            className="w-full rounded-md bg-[#1a1a18] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#157a59] disabled:opacity-50">
            {busy ? "Checking…" : "Unlock"}
          </button>
        </form>
      </div>
      <Footer />
    </div>
  );
}

const TIER_INFO = [
  { key: "machine_verified", label: "Machine-verified", color: "#1D9E75",
    desc: "Matched 1:1 to the fact in SEC's machine-readable XBRL instance for the exact filing — concept, context, member and value." },
  { key: "audit_verified", label: "AI-audited", color: "#378ADD",
    desc: "A second AI re-read the exact SEC source table behind the figure and independently confirmed (or corrected) it." },
  { key: "quote_validated", label: "Quote-validated", color: "#7F77DD",
    desc: "Extracted from filing prose; the verbatim quote and the number inside it were validated character-for-character against the filing." },
  { key: "derived", label: "Derived from SEC tables", color: "#BA7517",
    desc: "Computed arithmetically from the company's SEC-tagged revenue tables (e.g. a region's share of total revenue)." },
];

const ISSUE_LABEL = {
  history_mismatch: "Chart contradicts headline",
  history_jump: "Welded history (basis change)",
  trend_mismatch: "Trend label wrong",
  intercompany: "Intercompany self-label",
  basis_unflagged: "Customers sum > 100% (unflagged)",
  member_exceeds_group: "Member exceeds group total",
};

const VERDICT_STYLE = {
  VERIFIED: { bg: "#1D9E7515", fg: "#157a59", Icon: CheckCircle2, label: "Verified" },
  WRONG: { bg: "#E24B4A15", fg: "#b23b3a", Icon: AlertTriangle, label: "Was wrong" },
  UNCLEAR: { bg: "#BA751715", fg: "#8a570f", Icon: AlertTriangle, label: "Unclear" },
  ERROR: { bg: "#9a978d15", fg: "#6b6860", Icon: AlertTriangle, label: "Error" },
};

function Stat({ label, value, color }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white px-5 py-4" data-testid={`audit-stat-${label.toLowerCase()}`}>
      <div className="text-3xl font-heading font-bold" style={{ color: color || "#1a1a18" }}>{value}</div>
      <div className="mt-1 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">{label}</div>
    </div>
  );
}

export default function Accuracy() {
  const [unlocked, setUnlocked] = useState(hasAdminKey());
  const [status, setStatus] = useState(null);
  const [deep, setDeep] = useState(null);
  const [data, setData] = useState({ results: [], summary: {} });
  const [filter, setFilter] = useState("flagged");
  const [busy, setBusy] = useState(false);
  const [coh, setCoh] = useState(null);
  const [cohBusy, setCohBusy] = useState(false);
  const [slo, setSlo] = useState(null);
  const admin = unlocked;

  const scanCoherence = async () => {
    setCohBusy(true);
    try { setCoh(await coherenceScan()); } finally { setCohBusy(false); }
  };
  const fixCoherence = async () => {
    setCohBusy(true);
    try { await coherenceFix(); setCoh(await coherenceScan()); } finally { setCohBusy(false); }
  };

  const load = useCallback(() => {
    if (!hasAdminKey()) return;
    auditStatus().then(setStatus).catch(() => {});
    deepAuditStatus().then(setDeep).catch(() => {});
    auditResults(filter).then(setData).catch(() => {});
    accuracySlo().then(setSlo).catch(() => {});
  }, [filter]);

  useEffect(() => { if (unlocked) load(); }, [load, unlocked]);

  // Poll while a run is active so the user can watch progress, then stop.
  useEffect(() => {
    if (!status?.running && !deep?.running) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [status?.running, deep?.running, load]);

  const run = async () => {
    setBusy(true);
    try {
      await startAudit(100, "");
      setTimeout(load, 1500);
    } finally {
      setBusy(false);
    }
  };

  const runDeep = async () => {
    setBusy(true);
    try {
      await startDeepAudit(0.98);
      setTimeout(load, 1500);
    } finally {
      setBusy(false);
    }
  };

  const pct = useMemo(() => {
    if (!status?.total) return 0;
    return Math.min(100, Math.round((status.done / status.total) * 100));
  }, [status]);

  const deepPct = useMemo(() => {
    if (!deep?.total) return 0;
    return Math.min(100, Math.round((deep.done / deep.total) * 100));
  }, [deep]);

  const summary = data.summary || {};

  if (!unlocked) return <AdminGate onUnlock={() => setUnlocked(true)} />;

  return (
    <div className="min-h-screen bg-[#fbfaf6]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#1D9E75]">
          <ShieldCheck className="h-4 w-4" /> AI accuracy auditor
        </div>
        <h1 className="mt-3 font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#1a1a18]">
          Does the data check out?
        </h1>
        <p className="mt-4 max-w-2xl text-base text-neutral-600">
          Figures link to their SEC source — and are checked against it wherever an exact match exists.
          Structured figures are matched 1:1 to SEC's machine-readable XBRL data; a second AI
          re-reads the exact source table behind headline figures and auto-corrects confident
          mismatches; prose figures carry verbatim, character-checked quotes.
        </p>

        {slo && slo.total_figures > 0 && (
          <div className="mt-8 rounded-xl border border-[#1D9E7540] bg-white p-6" data-testid="verification-panel">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div className="text-sm font-semibold uppercase tracking-[0.15em] text-neutral-500">How we verify</div>
              <div className="text-xs text-neutral-400">
                {slo.total_figures} displayed figures · updated {slo.as_of ? new Date(slo.as_of).toLocaleDateString() : "—"}
              </div>
            </div>
            <div className="mt-2 font-heading text-3xl font-bold text-[#157a59]" data-testid="verification-headline">
              {slo.source_validated_pct}% of figures match their SEC source exactly
            </div>
            <p className="mt-1 max-w-3xl text-sm text-neutral-600">
              Source fidelity and interpretation are checked separately. Fidelity: the number is
              matched to SEC machine-readable data or re-read from the exact filing table.
              Meaning: a separate semantic audit judges whether each headline is a genuine
              ASC&nbsp;275/825 concentration at all — not a revenue mix, a diversification statement,
              or a reporting threshold.
              {slo.semantic?.judged ? ` So far: ${slo.semantic.judged} headlines judged — ${slo.semantic.verified} confirmed genuine, ${slo.semantic.corrected} corrected, ${slo.semantic.flagged} under scrutiny.` : ""}
            </p>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {TIER_INFO.map((t) => (
                <div key={t.key} className="rounded-lg border border-neutral-200 bg-[#fbfaf6] p-4" data-testid={`tier-${t.key}`}>
                  <div className="font-heading text-2xl font-bold" style={{ color: t.color }}>{slo.tiers[t.key] ?? 0}</div>
                  <div className="mt-0.5 text-xs font-semibold uppercase tracking-[0.12em] text-neutral-600">{t.label}</div>
                  <p className="mt-2 text-xs leading-relaxed text-neutral-500">{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {admin && (
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button
            data-testid="run-audit-btn"
            onClick={run}
            disabled={busy || status?.running}
            className="inline-flex items-center gap-2 rounded-full bg-[#1a1a18] px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {status?.running ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            {status?.running ? "Audit running…" : "Run audit · top 100"}
          </button>
          <button
            data-testid="refresh-audit-btn"
            onClick={load}
            className="inline-flex items-center gap-2 rounded-full border border-neutral-300 px-4 py-2.5 text-sm font-semibold text-neutral-700 hover:bg-white"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button
            data-testid="run-deep-audit-btn"
            onClick={runDeep}
            disabled={busy || deep?.running}
            title="Re-judge every figure below 98% confidence with Claude Opus (highest accuracy)"
            className="inline-flex items-center gap-2 rounded-full border border-[#7F77DD] bg-[#7F77DD0d] px-5 py-2.5 text-sm font-semibold text-[#5a52c2] transition-colors hover:bg-[#7F77DD1a] disabled:opacity-50"
          >
            {deep?.running ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {deep?.running ? "Deep audit running…" : "Deep re-audit edge cases · Opus"}
          </button>
        </div>
        )}

        {admin && deep && (deep.total > 0 || deep.running) && (
          <div className="mt-4 rounded-xl border border-[#7F77DD55] bg-[#7F77DD08] p-5" data-testid="deep-audit-progress">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-[#5a52c2]">
                <Sparkles className="mr-1.5 inline h-3.5 w-3.5" />
                {deep.running ? `Opus deep-auditing ${deep.current || "…"}` : "Deep audit complete (Claude Opus)"}
              </span>
              <span className="text-neutral-500">{deep.done || 0} / {deep.total || 0} edge cases</span>
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-neutral-100">
              <div className="h-full rounded-full bg-[#7F77DD] transition-all" style={{ width: `${deepPct}%` }} />
            </div>
            <div className="mt-2 text-xs text-neutral-500">
              Re-corrected {deep.corrected || 0} · re-verified {deep.verified || 0} · still flagged {deep.flagged || 0}
              {deep.errors ? ` · ${deep.errors} skipped (budget/parse — original verdict kept)` : ""}
            </div>
          </div>
        )}

        {admin && status && (status.total > 0 || status.running) && (
          <div className="mt-6 rounded-xl border border-neutral-200 bg-white p-5" data-testid="audit-progress">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-[#1a1a18]">
                {status.running ? `Auditing ${status.current || "…"}` : "Last run complete"}
              </span>
              <span className="text-neutral-500">{status.done || 0} / {status.total || 0} checks</span>
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-neutral-100">
              <div className="h-full rounded-full bg-[#1D9E75] transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Checked" value={summary.total ?? 0} />
          <Stat label="Verified" value={summary.verified ?? 0} color="#157a59" />
          <Stat label="Auto-corrected" value={summary.corrected ?? 0} color="#b23b3a" />
          <Stat label="Flagged" value={summary.flagged ?? 0} color="#8a570f" />
        </div>

        <div className="mt-8 rounded-xl border border-neutral-200 bg-white p-5" data-testid="coherence-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-[#1a1a18]">
                <Wrench className="h-4 w-4 text-[#378ADD]" /> Data coherence check
              </div>
              <p className="mt-1 max-w-xl text-xs text-neutral-500">
                Instant, $0 structural audit (no AI): flags charts that contradict the headline,
                wrong trend labels, intercompany self-labels, and customer breakdowns summing over 100%.
              </p>
            </div>
            <div className="flex gap-2">
              <button data-testid="coherence-scan-btn" onClick={scanCoherence} disabled={cohBusy}
                className="inline-flex items-center gap-2 rounded-full border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50">
                {cohBusy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Scan
              </button>
              {admin && (
              <button data-testid="coherence-fix-btn" onClick={fixCoherence}
                disabled={cohBusy || !(coh && coh.issue_count > 0)}
                className="inline-flex items-center gap-2 rounded-full bg-[#378ADD] px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
                <Wrench className="h-4 w-4" /> Auto-repair
              </button>
              )}
            </div>
          </div>
          {coh && (
            <div className="mt-4" data-testid="coherence-result">
              {coh.issue_count === 0 ? (
                <div className="flex items-center gap-2 rounded-lg bg-[#1D9E7510] px-4 py-3 text-sm font-medium text-[#157a59]">
                  <CheckCircle2 className="h-4 w-4" /> All {coh.checked} companies are coherent — no chart/breakdown mismatches.
                </div>
              ) : (
                <>
                  <div className="text-sm text-neutral-600">
                    <span className="font-semibold text-[#b23b3a]">{coh.issue_count}</span> issue(s) across{" "}
                    <span className="font-semibold">{coh.affected}</span> companies:
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {Object.entries(coh.counts).map(([k, v]) => (
                      <span key={k} className="rounded-full bg-[#E24B4A12] px-3 py-1 text-xs font-semibold text-[#b23b3a]">
                        {ISSUE_LABEL[k] || k}: {v}
                      </span>
                    ))}
                  </div>
                  <div className="mt-3 space-y-1.5 text-xs" data-testid="coherence-issues">
                    {coh.issues.slice(0, 30).map((it) => (
                      <div key={`${it.ticker}-${it.dimension}-${it.issue}`} className="flex flex-wrap items-center gap-2 border-b border-neutral-100 pb-1.5">
                        <Link to={`/company/${it.ticker}`} className="font-semibold text-[#1a1a18] hover:underline">{it.ticker}</Link>
                        <span className="uppercase tracking-wider text-neutral-400">{it.dimension}</span>
                        <span className="rounded bg-neutral-100 px-1.5 py-0.5 font-medium text-neutral-600">{ISSUE_LABEL[it.issue] || it.issue}</span>
                        <span className="text-neutral-500">{it.detail}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="audit-filters">
          {["flagged", "corrected", "verified", "all"].map((f) => (
            <button
              key={f}
              data-testid={`audit-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold capitalize transition-colors ${
                filter === f ? "bg-[#1a1a18] text-white" : "border border-neutral-300 text-neutral-600 hover:bg-white"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="mt-5 space-y-3" data-testid="audit-results">
          {data.results.length === 0 && (
            <p className="text-sm text-neutral-500">No records yet. Run the audit to populate results.</p>
          )}
          {data.results.map((r, i) => {
            const vs = VERDICT_STYLE[r.verdict] || VERDICT_STYLE.ERROR;
            const Icon = r.corrected ? Wrench : vs.Icon;
            return (
              <div key={`${r.ticker}-${r.dimension || r.dim || i}`} className="rounded-xl border border-neutral-200 bg-white p-4" data-testid="audit-row">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <Link to={`/company/${r.ticker}`} className="font-heading text-lg font-bold text-[#1a1a18] hover:underline">
                      {r.ticker}
                    </Link>
                    <span className="text-xs font-semibold uppercase tracking-wider text-neutral-400">{r.dimension}</span>
                    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                      style={{ background: vs.bg, color: vs.fg }}>
                      <Icon className="h-3.5 w-3.5" />
                      {r.corrected ? "Auto-corrected" : vs.label}
                    </span>
                    {r.confidence != null && (
                      <span className="text-xs text-neutral-400">conf {Math.round(r.confidence * 100)}%</span>
                    )}
                    {r.tier === 3 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#7F77DD15] px-2 py-0.5 text-xs font-semibold text-[#5a52c2]"
                        title="Re-judged by Claude Opus (tier-3 deep audit)">
                        <Sparkles className="h-3 w-3" /> Opus
                      </span>
                    )}
                  </div>
                  {r.source_url && (
                    <a href={r.source_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#378ADD] hover:underline">
                      Source <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                {r.corrected && r.before && r.after && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                    <span className="rounded bg-[#E24B4A12] px-2 py-0.5 text-[#b23b3a] line-through">
                      {r.before.entity} {r.before.pct}%
                    </span>
                    <span className="text-neutral-400">→</span>
                    <span className="rounded bg-[#1D9E7512] px-2 py-0.5 font-semibold text-[#157a59]">
                      {r.after.absent ? "No concentration" : `${r.after.entity} ${r.after.pct}%`}
                    </span>
                  </div>
                )}
                {r.reason && <p className="mt-2 text-sm leading-relaxed text-neutral-600">{r.reason}</p>}
              </div>
            );
          })}
        </div>
      </div>
      <Footer />
    </div>
  );
}
